import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import TireDataGrid from "../components/TireDataGrid";
import TrackWeekSelector from "../components/TrackWeekSelector";
import { useComparisonStore } from "../state/comparisonStore";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import type { H2HAnalysis, RunSummary, TireData } from "../types";
import { api } from "../utils/api";
import { formatDelta, formatLapTime, formatSetupName } from "../utils/formatters";

interface SetupDiffEntry {
  category: string;
  parameter: string;
  path: string;
  valueA: string;
  valueB: string;
  delta: number | null;
}

interface SetupDiffResult {
  runA?: { id: string; displayName: string | null; setupName: string };
  runB?: { id: string; displayName: string | null; setupName: string };
  categories?: Record<string, SetupDiffEntry[]>;
  totalDiffs?: number;
  message?: string;
}

const SESSION_TYPES = ["Test", "Practice", "Qualifying", "Race"] as const;
type SessionType = (typeof SESSION_TYPES)[number];
const SESSION_COLORS: Record<SessionType, string> = {
  Test: "var(--flag-purple)", Practice: "var(--live)", Qualifying: "var(--flag-orange)", Race: "var(--flag-red)",
};

/* Display labels for the stats table's raw backend metric keys */
const STAT_METRIC_LABELS: Record<string, string> = {
  bestLapTime: "Best lap",
  top10Avg: "Top 10 avg",
  top20Avg: "Top 20 avg",
  top30Avg: "Top 30 avg",
  avgLapTime: "Average lap",
  falloffMsPerLap: "Falloff per lap",
  greenLaps: "Green laps",
  totalLaps: "Total laps",
  fuelPerLap: "Fuel per lap",
};

function metricLabel(key: string): string {
  return STAT_METRIC_LABELS[key] ?? key;
}

export default function HeadToHead() {
  const { trackWeekId: routeTrackWeekId } = useParams<{ trackWeekId: string }>();
  const { activeTeamId } = useTeamStore();
  const { runs, selectedTrackWeekId, fetchTrackWeeks, fetchRuns } = useRunStore();
  const { headToHead, loading, fetchHeadToHead } = useComparisonStore();

  const [runAId, setRunAId] = useState("");
  const [runBId, setRunBId] = useState("");
  const [setupDiff, setSetupDiff] = useState<SetupDiffResult | null>(null);
  const [setupDiffLoading, setSetupDiffLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<SessionType>>(new Set(["Test", "Practice", "Qualifying"]));

  const toggleFilter = (type: SessionType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) { if (next.size > 1) next.delete(type); } else { next.add(type); }
      return next;
    });
  };

  useEffect(() => {
    if (activeTeamId) {
      void fetchTrackWeeks(activeTeamId);
    }
  }, [activeTeamId, fetchTrackWeeks]);

  useEffect(() => {
    if (activeTeamId) {
      void fetchRuns(activeTeamId, selectedTrackWeekId || undefined);
    }
  }, [activeTeamId, selectedTrackWeekId, fetchRuns]);

  const filteredRuns = useMemo(
    () => runs.filter((r) => activeFilters.has((r.eventType || "Test") as SessionType)),
    [runs, activeFilters]
  );

  const runsByDriver = useMemo(() => {
    return filteredRuns.reduce<Record<string, RunSummary[]>>((accumulator, run) => {
      const key = run.displayName || run.userId;
      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(run);
      return accumulator;
    }, {});
  }, [filteredRuns]);

  const selectedRunA = runs.find((run) => run.id === runAId);
  const selectedRunB = runs.find((run) => run.id === runBId);

  const handleCompare = async () => {
    if (!runAId || !runBId) return;
    setSetupDiffLoading(true);
    try {
      await Promise.all([
        fetchHeadToHead(runAId, runBId),
        api<SetupDiffResult>(`/api/comparison/setup-diff?run_a=${runAId}&run_b=${runBId}`).then((result) => setSetupDiff(result)),
      ]);
    } finally {
      setSetupDiffLoading(false);
    }
  };

  return (
    <div>
      {/* Tab content under the WeekWorkspace hero — no page title; slim helper only */}
      <p style={{ color: "var(--text-secondary)", fontSize: "0.88rem", margin: "0 0 1.25rem", maxWidth: "60ch" }}>
        Pick any two runs from the week and compare pace, lap deltas, tire state, and setup differences in one view.
      </p>

      {!routeTrackWeekId && (
        <div style={{ marginBottom: "1rem" }}>
          <TrackWeekSelector />
        </div>
      )}

      {/* Session type filters */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "1rem" }}>
        {SESSION_TYPES.map((type) => {
          const active = activeFilters.has(type);
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              style={{
                padding: "0.3rem 0.6rem",
                borderRadius: 6,
                border: `1px solid ${active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 38%, transparent)` : "var(--rule-faint)"}`,
                background: active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 12%, transparent)` : "transparent",
                color: active ? SESSION_COLORS[type] : "var(--text-muted)",
                fontFamily: "var(--font-body)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "border-color var(--t-fast), background-color var(--t-fast), color var(--t-fast)",
              }}
            >
              {type}
            </button>
          );
        })}
      </div>

      <section className="card" style={styles.selectorCard}>
        <div style={styles.selectorGrid}>
          <RunSelector label="Run A" accent="pill pill--accent" value={runAId} onChange={setRunAId} runsByDriver={runsByDriver} selectedRun={selectedRunA} />
          <div style={styles.selectorCenter}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCompare}
              disabled={!runAId || !runBId || loading || setupDiffLoading}
            >
              {loading || setupDiffLoading ? "Comparing..." : "Compare Runs"}
            </button>
          </div>
          <RunSelector label="Run B" accent="pill" value={runBId} onChange={setRunBId} runsByDriver={runsByDriver} selectedRun={selectedRunB} />
        </div>
      </section>

      {headToHead && (
        <div className="fade-in">
          {headToHead.analysis && headToHead.analysis.keyTakeaways.length > 0 && (
            <section className="section-shell">
              <div className="section-header">
                <div>
                  <div className="section-kicker">Analysis</div>
                  <div className="section-heading">Takeaways</div>
                </div>
                {headToHead.analysis.weatherSimilarity != null && (
                  <span className="pill">{headToHead.analysis.weatherSimilarity.toFixed(0)}% weather match</span>
                )}
              </div>
              <div style={styles.takeawayGrid}>
                {headToHead.analysis.keyTakeaways.map((takeaway) => (
                  <div key={takeaway} className="card" style={styles.takeawayCard}>
                    <div style={styles.takeawayTitle}>{takeaway}</div>
                    <div style={styles.takeawayMeta}>
                      {renderAnalysisChips(headToHead.analysis!).map((chip) => (
                        <span key={chip} className="pill">{chip}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="section-shell">
            <div className="section-header">
              <div>
                <div className="section-kicker">Stats</div>
                <div className="section-heading">Run metrics</div>
              </div>
            </div>
            <div className="card xpe-table-scroll" style={{ padding: 0, overflow: "hidden" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Run A</th>
                    <th>Run B</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {headToHead.statsDelta &&
                    Object.entries((headToHead.statsDelta as Record<string, Record<string, unknown>>).runA || {}).map(([key, valueA]) => {
                      const valueB = (headToHead.statsDelta as Record<string, Record<string, unknown>>).runB?.[key];
                      const delta = (headToHead.statsDelta as Record<string, Record<string, unknown>>).delta?.[key];
                      const isTime = typeof valueA === "number" && valueA > 10;
                      return (
                        <tr key={key}>
                          <td>{metricLabel(key)}</td>
                          <td>{isTime ? formatLapTime(valueA as number) : String(valueA ?? "-")}</td>
                          <td>{isTime ? formatLapTime(valueB as number) : String(valueB ?? "-")}</td>
                          <td className="mono">{typeof delta === "number" ? formatDelta(delta) : "-"}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section-shell">
            <div className="section-header">
              <div>
                <div className="section-kicker">Lap Deltas</div>
                <div className="section-heading">Lap-by-lap split</div>
              </div>
              <span className="pill">{headToHead.lapDeltas.length} laps</span>
            </div>
            <div className="card xpe-table-scroll" style={{ padding: 0, overflow: "hidden" }}>
              <table className="table striped">
                <thead>
                  <tr>
                    <th>Lap</th>
                    <th>Run A</th>
                    <th>Run B</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {headToHead.lapDeltas.map((deltaRow) => (
                    <tr key={deltaRow.lapNum}>
                      <td>{deltaRow.lapNum}</td>
                      <td>{formatLapTime(deltaRow.timeA)}</td>
                      <td>{formatLapTime(deltaRow.timeB)}</td>
                      <td className="mono">{formatDelta(deltaRow.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section-shell">
            <div className="section-header">
              <div>
                <div className="section-kicker">Setup Differences</div>
                <div className="section-heading">Setup differences</div>
              </div>
              {setupDiff?.totalDiffs != null && <span className="pill">{setupDiff.totalDiffs} differences</span>}
            </div>
            <div className="card" style={{ padding: "1rem" }}>
              {setupDiffLoading ? (
                <div className="empty-state" style={{ minHeight: "12rem" }}>
                  <div className="spinner spinner-lg" />
                  <div className="empty-state-title">Loading setup differences</div>
                </div>
              ) : setupDiff?.message ? (
                <div className="empty-state" style={{ minHeight: "12rem" }}>
                  <div className="empty-state-title">No setup diff available</div>
                  <div className="empty-state-text">{setupDiff.message}</div>
                </div>
              ) : setupDiff?.categories && Object.keys(setupDiff.categories).length > 0 ? (
                <div style={styles.setupGroupList}>
                  {Object.entries(setupDiff.categories).map(([category, entries]) => (
                    <div key={category} className="card" style={{ padding: 0, overflow: "hidden" }}>
                      <div style={styles.setupGroupHeader}>{category}</div>
                      <div style={styles.setupEntries}>
                        {entries.map((entry) => (
                          <div key={entry.path} style={styles.setupEntry}>
                            <div style={styles.setupParameter}>{entry.parameter}</div>
                            <div className="mono" style={styles.setupValueA}>{entry.valueA}</div>
                            <div className="mono" style={styles.setupValueB}>{entry.valueB}</div>
                            <div className="mono" style={styles.setupDelta}>{entry.delta == null ? "N/A" : `${entry.delta > 0 ? "+" : ""}${entry.delta}`}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ minHeight: "12rem" }}>
                  <div className="empty-state-title">Compare two runs</div>
                  <div className="empty-state-text">Pick two runs above to load the setup difference view.</div>
                </div>
              )}
            </div>
          </section>

          <section className="section-shell">
            <div className="section-header">
              <div>
                <div className="section-kicker">Tire Comparison</div>
                <div className="section-heading">Tire comparison</div>
              </div>
            </div>
            <div style={styles.tireGrid}>
              <div className="card" style={{ padding: "1rem" }}>
                <div className="section-kicker" style={{ marginBottom: "0.75rem" }}>Run A</div>
                <TireDataGrid tireData={headToHead.tireComparison?.runA as TireData | null} />
              </div>
              <div className="card" style={{ padding: "1rem" }}>
                <div className="section-kicker" style={{ marginBottom: "0.75rem" }}>Run B</div>
                <TireDataGrid tireData={headToHead.tireComparison?.runB as TireData | null} />
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function RunSelector({
  label,
  accent,
  value,
  onChange,
  runsByDriver,
  selectedRun,
}: {
  label: string;
  accent: string;
  value: string;
  onChange: (value: string) => void;
  runsByDriver: Record<string, RunSummary[]>;
  selectedRun?: RunSummary;
}) {
  return (
    <div style={styles.selectorPane}>
      <span className={accent}>{label}</span>
      <select className="select" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select a run...</option>
        {Object.entries(runsByDriver).map(([driver, driverRuns]) => (
          <optgroup key={driver} label={driver}>
            {driverRuns.map((run) => (
              <option key={run.id} value={run.id}>
                {formatSetupName(run.setupName)} • {formatLapTime(run.bestLapTime)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {selectedRun && (
        <div style={styles.selectedRunCard}>
          <div style={styles.selectedRunName}>{selectedRun.displayName}</div>
          <div style={styles.selectedRunMetric}>{formatLapTime(selectedRun.bestLapTime)}</div>
          <div style={styles.selectedRunMeta}>{formatSetupName(selectedRun.setupName)}</div>
        </div>
      )}
    </div>
  );
}

function renderAnalysisChips(analysis: H2HAnalysis): string[] {
  const chips: string[] = [];
  if (analysis.durabilityWinner && analysis.durabilityWinner !== "tie") chips.push(`Durability: ${analysis.durabilityWinner}`);
  if (analysis.tireEfficiency && analysis.tireEfficiency !== "tie") chips.push(`Tire efficiency: ${analysis.tireEfficiency}`);
  if (analysis.weatherSimilarity != null) chips.push(`Weather ${analysis.weatherSimilarity.toFixed(0)}%`);
  return chips.length > 0 ? chips : ["Balanced comparison"];
}

const styles: Record<string, React.CSSProperties> = {
  selectorCard: {
    padding: "1rem",
    marginBottom: "1rem",
  },
  selectorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1rem",
    alignItems: "stretch",
  },
  selectorPane: {
    display: "flex",
    flexDirection: "column",
    gap: "0.8rem",
  },
  selectorCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  selectedRunCard: {
    padding: "0.9rem 1rem",
    borderRadius: "var(--r-md)",
    background: "var(--ink-1)",
    border: "1px solid var(--rule-strong)",
  },
  selectedRunName: {
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--text-strong)",
  },
  selectedRunMetric: {
    marginTop: "0.3rem",
    fontFamily: "var(--font-mono)",
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--live-strong)",
  },
  selectedRunMeta: {
    marginTop: "0.3rem",
    color: "var(--text-secondary)",
  },
  takeawayGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "0.9rem",
  },
  takeawayCard: {
    padding: "1rem 1.05rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.8rem",
  },
  takeawayTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--text-strong)",
    lineHeight: 1.45,
  },
  takeawayMeta: {
    display: "flex",
    gap: "0.55rem",
    flexWrap: "wrap",
  },
  setupGroupList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.9rem",
  },
  setupGroupHeader: {
    padding: "0.85rem 1rem",
    borderBottom: "1px solid var(--rule-strong)",
    background: "var(--ink-1)",
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--text-strong)",
  },
  setupEntries: {
    display: "flex",
    flexDirection: "column",
  },
  setupEntry: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) repeat(3, minmax(120px, 1fr))",
    gap: "0.8rem",
    padding: "0.9rem 1rem",
    borderTop: "1px solid var(--rule-faint)",
    alignItems: "center",
  },
  setupParameter: {
    color: "var(--text-default)",
    fontWeight: 600,
  },
  setupValueA: {
    color: "var(--live-strong)",
  },
  setupValueB: {
    color: "var(--text-default)",
  },
  setupDelta: {
    color: "var(--text-secondary)",
  },
  tireGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1rem",
  },
};
